import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { getStations } from "@repo/ui/api/fetchStations";
import type { GroupedStations } from "@repo/ui/types/stations";

export interface StationsState {
  stations: GroupedStations | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: StationsState = {
  stations: null,
  isLoading: false,
  error: null,
};

// Helper to reduce Redux state size by removing unused fields
const stripUnusedStationData = (data: GroupedStations): GroupedStations => {
  const stripped: GroupedStations = {};
  for (const [country, cities] of Object.entries(data)) {
    stripped[country] = cities.map((city) => {
      const {
        coordinates,
        class: _class,
        cultureCode,
        currencyCode,
        inActive,
        timeZoneCode,
        ...essentialCityData
      } = city;
      return essentialCityData;
    });
  }
  return stripped;
};

export const fetchStationsThunk = createAsyncThunk(
  "stations/fetchStations",
  async (tabSessionId?: string | null) => {
    const data = await getStations(tabSessionId);
    if (!data) return null;
    return stripUnusedStationData(data);
  },
);

export const stationsSlice = createSlice({
  name: "stations",
  initialState,
  reducers: {
    setStations: (state, action: PayloadAction<GroupedStations>) => {
      state.stations = action.payload;
    },
    setStationsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStationsError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStationsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStationsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stations = action.payload;
      })
      .addCase(fetchStationsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? "Failed to load stations";
      });
  },
});

export const { setStations, setStationsLoading, setStationsError } = stationsSlice.actions;
export default stationsSlice.reducer;
